import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

type ControlPhase = 'signal' | 'reveal' | 'fleet' | 'disruption' | 'depth' | 'always' | 'exit';

interface ControlOptions { reducedMotion: boolean; coarsePointer: boolean; }
interface VideoScrubber { load(): void; setProgress(progress: number): void; pause(): void; }

const PHASES: Array<{ phase: ControlPhase; start: number }> = [
  { phase: 'signal', start: 0 },
  { phase: 'reveal', start: 0.15 },
  { phase: 'fleet', start: 0.32 },
  { phase: 'disruption', start: 0.5 },
  { phase: 'depth', start: 0.68 },
  { phase: 'always', start: 0.82 },
  { phase: 'exit', start: 0.94 },
];

const clamp = (value: number): number => Math.min(1, Math.max(0, value));
const localProgress = (progress: number, start: number, end: number): number =>
  clamp((progress - start) / Math.max(0.001, end - start));

function currentPhase(progress: number): ControlPhase {
  let phase: ControlPhase = 'signal';
  for (const candidate of PHASES) if (progress >= candidate.start) phase = candidate.phase;
  return phase;
}

function activeVideo(progress: number): number {
  if (progress < 0.32) return 0;
  if (progress < 0.5) return 1;
  if (progress < 0.82) return 2;
  return 3;
}

function createVideoScrubber(video: HTMLVideoElement): VideoScrubber {
  let duration = 0;
  let progress = 0;
  let desiredTime = 0;
  let loaded = false;
  let active = false;
  let animationFrame = 0;

  video.muted = true;
  video.playsInline = true;
  video.pause();

  const syncMetadata = (): void => {
    duration = Number.isFinite(video.duration) ? Math.max(0, video.duration - 0.05) : 0;
    desiredTime = duration * progress;
    video.pause();
    if (duration > 0 && video.currentTime === 0) video.currentTime = 0.001;
  };
  video.addEventListener('loadedmetadata', syncMetadata);
  if (video.readyState >= HTMLMediaElement.HAVE_METADATA) syncMetadata();

  const render = (): void => {
    if (active && duration > 0 && !video.seeking && Math.abs(video.currentTime - desiredTime) > 1 / 48) {
      video.currentTime = desiredTime;
    }
    animationFrame = requestAnimationFrame(render);
  };
  animationFrame = requestAnimationFrame(render);

  document.addEventListener('visibilitychange', () => { if (document.hidden) video.pause(); });
  window.addEventListener('pagehide', () => cancelAnimationFrame(animationFrame), { once: true });

  return {
    load(): void {
      active = true;
      if (loaded) return;
      loaded = true;
      video.preload = 'auto';
      video.load();
    },
    setProgress(nextProgress: number): void {
      active = true;
      progress = clamp(nextProgress);
      if (duration > 0) desiredTime = duration * progress;
    },
    pause(): void { active = false; video.pause(); },
  };
}

function setPhaseRail(section: HTMLElement, phase: ControlPhase): void {
  section.querySelectorAll<HTMLElement>('[data-control-step]').forEach((item) => {
    const step = item.dataset.controlStep;
    item.classList.toggle('is-active', step === phase || (phase === 'exit' && step === 'always'));
  });
}

function preloadNearSection(section: HTMLElement, scrubbers: VideoScrubber[]): void {
  const observer = new IntersectionObserver((entries) => {
    if (!entries.some((entry) => entry.isIntersecting)) return;
    scrubbers[0]?.load();
    scrubbers[1]?.load();
    observer.disconnect();
  }, { rootMargin: '180% 0px 180% 0px' });
  observer.observe(section);
}

function initHubInteraction(section: HTMLElement): void {
  section.querySelectorAll<HTMLButtonElement>('.control-hub').forEach((hub) => {
    const activate = (): void => {
      section.dataset.activeControlHub = hub.dataset.controlHub ?? '';
      hub.classList.add('is-active');
    };
    const deactivate = (): void => {
      delete section.dataset.activeControlHub;
      hub.classList.remove('is-active');
    };
    hub.addEventListener('pointerenter', activate);
    hub.addEventListener('pointerleave', deactivate);
    hub.addEventListener('focus', activate);
    hub.addEventListener('blur', deactivate);
  });
}

export function initAlwaysMoving({ reducedMotion, coarsePointer }: ControlOptions): void {
  const section = document.querySelector<HTMLElement>('#always-moving');
  const stage = document.querySelector<HTMLElement>('#control-stage');
  const videos = Array.from(document.querySelectorAll<HTMLVideoElement>('.control-video'));
  const entrySignal = document.querySelector<HTMLElement>('#control-entry-signal');
  const entryBranches = document.querySelector<SVGSVGElement>('#control-entry-signal svg');
  const entryPulse = document.querySelector<HTMLElement>('#control-entry-signal > span');
  const intro = document.querySelector<HTMLElement>('#control-intro');
  const eyebrow = document.querySelector<HTMLElement>('.control-eyebrow');
  const titleLine = document.querySelector<HTMLElement>('.control-title-mask > span');
  const introCopy = document.querySelector<HTMLElement>('.control-intro-copy');
  const digitalTwin = document.querySelector<HTMLElement>('#control-digital-twin');
  const hubs = document.querySelector<HTMLElement>('#control-hubs');
  const freightSignals = Array.from(document.querySelectorAll<SVGCircleElement>('.control-freight-signals circle'));
  const fleetCopy = document.querySelector<HTMLElement>('#control-fleet-copy');
  const liveMetrics = document.querySelector<HTMLElement>('#control-live-metrics');
  const eventStream = document.querySelector<HTMLElement>('#control-event-stream');
  const events = Array.from(document.querySelectorAll<HTMLElement>('.control-event'));
  const disruption = document.querySelector<HTMLElement>('#control-disruption');
  const reroute = document.querySelector<HTMLElement>('#control-reroute');
  const rerouteFooter = document.querySelector<HTMLElement>('#control-reroute footer');
  const oldRoute = document.querySelector<SVGPathElement>('#control-corridor-old');
  const optionA = document.querySelector<SVGPathElement>('#control-corridor-option-a');
  const optionB = document.querySelector<SVGPathElement>('#control-corridor-option-b');
  const responseCopy = document.querySelector<HTMLElement>('#control-response-copy');
  const responseLines = Array.from(document.querySelectorAll<HTMLElement>('.control-response-mask > span'));
  const depthGrid = document.querySelector<HTMLElement>('#control-depth-grid');
  const alwaysCopy = document.querySelector<HTMLElement>('#control-always-copy');
  const alwaysLines = Array.from(document.querySelectorAll<HTMLElement>('.control-always-mask > span'));
  const timeLayer = document.querySelector<HTMLElement>('#control-time-layer');
  const networkStatus = document.querySelector<HTMLElement>('#control-network-status');
  const progressRail = document.querySelector<HTMLElement>('#control-progress');
  const columnWipe = document.querySelector<HTMLElement>('#control-column-wipe');
  const exitBlackout = document.querySelector<HTMLElement>('#control-exit-blackout');
  const exitSignal = document.querySelector<HTMLElement>('#control-exit-signal');
  const exitLine = document.querySelector<HTMLElement>('#control-exit-signal i');
  const exitPoint = document.querySelector<HTMLElement>('#control-exit-signal span');
  const atmosphere = document.querySelector<HTMLElement>('#control-atmosphere');
  const vignette = document.querySelector<HTMLElement>('#control-vignette');

  if (
    !section || !stage || videos.length !== 4 || !entrySignal || !entryBranches || !entryPulse ||
    !intro || !eyebrow || !titleLine || !introCopy || !digitalTwin || !hubs || freightSignals.length < 10 ||
    !fleetCopy || !liveMetrics || !eventStream || events.length !== 4 || !disruption || !reroute || !rerouteFooter ||
    !oldRoute || !optionA || !optionB || !responseCopy || responseLines.length !== 2 || !depthGrid ||
    !alwaysCopy || alwaysLines.length !== 3 || !timeLayer || !networkStatus || !progressRail || !columnWipe ||
    !exitBlackout || !exitSignal || !exitLine || !exitPoint || !atmosphere || !vignette
  ) return;

  const scrubbers = videos.map(createVideoScrubber);
  preloadNearSection(section, scrubbers);
  if (!coarsePointer && !reducedMotion) initHubInteraction(section);

  if (reducedMotion) {
    stage.style.visibility = 'visible';
    section.dataset.controlPhase = 'fleet';
    section.dataset.activeVideo = 'static';
    section.dataset.storyProgress = '0.4200';
    return;
  }

  const isMobile = window.matchMedia('(max-width: 760px)').matches;
  let loadTier = 1;

  gsap.set(stage, { visibility: 'hidden' });
  gsap.set(videos, { opacity: 0, scale: 1.015, filter: 'brightness(.6) saturate(.74)' });
  gsap.set(videos[0], { opacity: 1 });
  gsap.set([intro, digitalTwin, hubs, fleetCopy, liveMetrics, eventStream, disruption, reroute, responseCopy, depthGrid, alwaysCopy, timeLayer, networkStatus, progressRail, columnWipe, exitBlackout, exitSignal], { opacity: 0 });
  gsap.set(eyebrow, { opacity: 0, y: 9, letterSpacing: '.5em' });
  gsap.set(titleLine, { yPercent: 116 });
  gsap.set(introCopy, { opacity: 0, y: 10, filter: 'blur(5px)' });
  gsap.set(events, { opacity: 0, y: 7 });
  gsap.set([disruption, reroute], { y: 9 });
  gsap.set([optionA, optionB], { opacity: 0 });
  gsap.set(responseLines, { yPercent: 114 });
  gsap.set(alwaysLines, { yPercent: 114 });
  gsap.set(exitLine, { scaleX: 1, xPercent: 0 });
  gsap.set(exitPoint, { x: 0 });
  gsap.set(columnWipe, { xPercent: -145 });

  const routeALength = optionA.getTotalLength();
  const routeBLength = optionB.getTotalLength();
  gsap.set(optionA, { strokeDasharray: routeALength, strokeDashoffset: routeALength });
  gsap.set(optionB, { strokeDasharray: routeBLength, strokeDashoffset: routeBLength });

  const story = { progress: 0 };
  const syncStory = (): void => {
    const progress = clamp(story.progress);
    const phase = currentPhase(progress);
    const activeIndex = activeVideo(progress);

    scrubbers[0]?.setProgress(localProgress(progress, 0, 0.32));
    scrubbers[1]?.setProgress(localProgress(progress, 0.32, 0.5));
    scrubbers[2]?.setProgress(localProgress(progress, 0.5, 0.82));
    scrubbers[3]?.setProgress(localProgress(progress, 0.82, 0.96));

    if (progress >= 0.24 && loadTier < 2) { loadTier = 2; scrubbers[2]?.load(); }
    if (progress >= 0.62 && loadTier < 3) { loadTier = 3; scrubbers[3]?.load(); }

    section.dataset.storyProgress = progress.toFixed(4);
    section.dataset.activeVideo = String(activeIndex);
    if (section.dataset.controlPhase !== phase) {
      section.dataset.controlPhase = phase;
      setPhaseRail(section, phase);
    }
  };

  const pauseAll = (): void => scrubbers.forEach((scrubber) => scrubber.pause());

  const timeline = gsap.timeline({
    defaults: { ease: 'power2.inOut' },
    scrollTrigger: {
      trigger: section,
      start: 'top top',
      end: () => `+=${Math.round(window.innerHeight * (isMobile ? 3.1 : 3.4))}`,
      scrub: isMobile ? 0.28 : 0.48,
      pin: stage,
      pinSpacing: false,
      anticipatePin: 1,
      invalidateOnRefresh: true,
      onEnter: () => { stage.style.visibility = 'visible'; scrubbers[0]?.load(); },
      onEnterBack: () => { stage.style.visibility = 'visible'; scrubbers[3]?.load(); },
      onLeave: () => { story.progress = 1; syncStory(); pauseAll(); },
      onLeaveBack: () => { stage.style.visibility = 'hidden'; story.progress = 0; syncStory(); pauseAll(); },
      onRefresh: (self) => {
        section.dataset.scrollStart = self.start.toFixed(2);
        section.dataset.scrollEnd = self.end.toFixed(2);
        if (self.progress > 0) stage.style.visibility = 'visible';
      },
    },
  });

  timeline
    .to(story, { progress: 1, duration: 1, ease: 'none', onUpdate: syncStory }, 0)

    // Section 06's cold network heartbeat opens the physical control room one reflection at a time.
    .fromTo(entryPulse, { scale: 0.55 }, { scale: 2.15, duration: 0.012, repeat: 3, yoyo: true, ease: 'power2.inOut' }, 0)
    .to(videos[0], { filter: 'brightness(.76) saturate(.78)', scale: 1, duration: 0.15, ease: 'none' }, 0)
    .to(entryBranches, { opacity: 0.7, duration: 0.06 }, 0.035)
    .to(intro, { opacity: 1, duration: 0.035 }, 0.045)
    .to(eyebrow, { opacity: 1, y: 0, letterSpacing: '.32em', duration: 0.05 }, 0.05)
    .to(titleLine, { yPercent: 0, duration: 0.065, ease: 'power3.out' }, 0.057)
    .to(introCopy, { opacity: 1, y: 0, filter: 'blur(0px)', duration: 0.045 }, 0.08)
    .to(progressRail, { opacity: 1, duration: 0.04 }, 0.095)
    .to(entrySignal, { opacity: 0, duration: 0.035 }, 0.115)

    // The panorama wall resolves into a live, operational version of the Europe network.
    .to(digitalTwin, { opacity: 0.52, duration: 0.055 }, 0.15)
    .to(hubs, { opacity: 1, duration: 0.05 }, 0.18)
    .to(freightSignals, { x: 62, y: -18, duration: 0.13, stagger: 0.006, ease: 'none' }, 0.18)
    .to(intro, { y: -22, opacity: 0.2, duration: 0.07 }, 0.22)
    .to(atmosphere, { opacity: 0.24, x: '1.2vw', duration: 0.12, ease: 'none' }, 0.18)

    // All vehicles and shipments resolve into one restrained operational picture.
    .to(columnWipe, { opacity: 1, xPercent: 145, duration: 0.045 }, 0.30)
    .set(videos[0], { opacity: 0 }, 0.32)
    .set(videos[1], { opacity: 1, scale: 1.012, filter: 'brightness(.76) saturate(.78)' }, 0.32)
    .to(columnWipe, { opacity: 0, duration: 0.025 }, 0.325)
    .to(intro, { opacity: 0, duration: 0.025 }, 0.32)
    .to(fleetCopy, { opacity: 1, duration: 0.04 }, 0.335)
    .to(liveMetrics, { opacity: 1, duration: 0.04 }, 0.355)
    .to(eventStream, { opacity: 1, duration: 0.025 }, 0.37)
    .to(events[0], { opacity: 1, y: 0, duration: 0.025 }, 0.375)
    .to(events[0], { opacity: 0, duration: 0.02 }, 0.415)
    .to(events[1], { opacity: 1, y: 0, duration: 0.025 }, 0.405)
    .to(events[1], { opacity: 0, duration: 0.02 }, 0.455)
    .to(events[2], { opacity: 1, y: 0, duration: 0.025 }, 0.445)
    .to(events[2], { opacity: 0, duration: 0.02 }, 0.485)
    .to([fleetCopy, liveMetrics, eventStream], { opacity: 0, y: -7, duration: 0.025 }, 0.475)

    // The network detects a condition, evaluates two routes, then commits the lower-variance path.
    .to(columnWipe, { opacity: 1, xPercent: -145, duration: 0 }, 0.47)
    .to(columnWipe, { xPercent: 145, duration: 0.045 }, 0.47)
    .set(videos[1], { opacity: 0 }, 0.5)
    .set(videos[2], { opacity: 1, scale: 1.01, filter: 'brightness(.71) saturate(.82)' }, 0.5)
    .to(columnWipe, { opacity: 0, duration: 0.025 }, 0.505)
    .to(disruption, { opacity: 1, y: 0, duration: 0.04 }, 0.515)
    .to(reroute, { opacity: 1, y: 0, duration: 0.04 }, 0.535)
    .to(optionA, { opacity: 0.45, strokeDashoffset: 0, duration: 0.07 }, 0.545)
    .to(optionB, { opacity: 0.68, strokeDashoffset: 0, duration: 0.07 }, 0.575)
    .to(oldRoute, { opacity: 0.16, duration: 0.04 }, 0.61)
    .to(optionA, { opacity: 0.12, duration: 0.035 }, 0.615)
    .to(optionB, { opacity: 0.92, stroke: 'rgba(234,244,247,.82)', duration: 0.045 }, 0.615)
    .to(rerouteFooter, { opacity: 1, duration: 0.035 }, 0.62)
    .to([disruption, reroute], { opacity: 0, y: -7, duration: 0.028 }, 0.655)
    .to(responseCopy, { opacity: 1, duration: 0.025 }, 0.64)
    .to(responseLines, { yPercent: 0, duration: 0.055, stagger: 0.012, ease: 'power3.out' }, 0.645)
    .to(responseCopy, { opacity: 0, y: -9, duration: 0.025 }, 0.675)

    // Camera crosses the wall plane; the same flat network separates into operational depth.
    .to(depthGrid, { opacity: 0.6, duration: 0.04 }, 0.68)
    .to(digitalTwin, { scale: isMobile ? 1.08 : 1.32, xPercent: isMobile ? -3 : -7, yPercent: -3, filter: 'blur(0.2px)', duration: 0.13, ease: 'none' }, 0.68)
    .to(hubs, { xPercent: isMobile ? 0 : -2.5, yPercent: -1.5, duration: 0.13, ease: 'none' }, 0.68)
    .to(freightSignals, { x: 145, y: -65, scale: 1.4, duration: 0.12, stagger: 0.004, ease: 'none' }, 0.69)
    .to(depthGrid, { scale: 1.36, opacity: 0.12, duration: 0.11, ease: 'none' }, 0.71)
    .to([digitalTwin, hubs, depthGrid], { opacity: 0, duration: 0.025 }, 0.805)

    // Physical room returns as night becomes dawn; operations stay active through the time change.
    .to(columnWipe, { opacity: 1, xPercent: -145, duration: 0 }, 0.795)
    .to(columnWipe, { xPercent: 145, duration: 0.045 }, 0.795)
    .set(videos[2], { opacity: 0 }, 0.82)
    .set(videos[3], { opacity: 1, scale: 1.015, filter: 'brightness(.77) saturate(.78)' }, 0.82)
    .to(columnWipe, { opacity: 0, duration: 0.025 }, 0.825)
    .to(videos[3], { scale: 1, filter: 'brightness(.92) saturate(.84)', duration: 0.13, ease: 'none' }, 0.82)
    .to(alwaysCopy, { opacity: 1, duration: 0.035 }, 0.83)
    .to(alwaysLines, { yPercent: 0, duration: 0.055, stagger: 0.01, ease: 'power3.out' }, 0.835)
    .to(timeLayer, { opacity: 1, duration: 0.035 }, 0.86)
    .to(networkStatus, { opacity: 1, duration: 0.035 }, 0.875)

    // One live signal leaves Europe, becomes a line, then stops as Section 08's sole point of light.
    .to([alwaysCopy, timeLayer, networkStatus, progressRail], { opacity: 0, y: -7, duration: 0.025 }, 0.925)
    .to(exitBlackout, { opacity: 1, duration: 0.035 }, 0.94)
    .to(exitSignal, { opacity: 1, duration: 0.025 }, 0.95)
    .to(exitLine, { xPercent: 43, duration: 0.03, ease: 'power2.in' }, 0.955)
    .to(exitPoint, { x: '20vw', duration: 0.03, ease: 'power2.in' }, 0.955)
    .to(exitLine, { scaleX: 0, duration: 0.025, ease: 'power2.out' }, 0.975)
    .to(exitPoint, { x: '22vw', duration: 0.02, ease: 'power3.out' }, 0.98)
    .to(vignette, { opacity: 0.88, duration: 0.025 }, 0.975);

  requestAnimationFrame(() => ScrollTrigger.refresh());
}
